import Button from '@mui/material/Button'
import { FacebookAlbumSelector, PinterestBoardSelector } from 'components/Composer/AlbumSelector'
import { AiContentCheckbox } from 'components/Composer/CustomizePostBlock/AiContentCheckbox'
import { AiContentFacebookPhotoNote } from 'components/Composer/CustomizePostBlock/AiContentFacebookPhotoNote'
import { PinterestRefreshBoardsButton } from 'components/Composer/AlbumSelector/PinterestRefreshBoardsButton'
import { InstagramLocationSelector } from 'components/Composer/CustomizePostBlock/InstagramLocationSelector'
import { TikTokProfileSettings } from 'components/Composer/CustomizePostBlock/TikTokOptions/TikTokProfileSettings'
import { PPDialog } from 'components/PPDialog'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import PPSwitch from 'components/PPSwitch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { pinterestProfilesBoardsSelector } from 'services/compose/selectors'
import { BulkContentPost } from 'services/compose/state'
import { AI_CONTENT_NETWORKS } from 'services/compose/utils'
import {
  BRAND_TIKTOK,
  BRAND_YOUTUBE,
  FB_POST_TYPE_REEL,
  POST_TYPE_STORY,
  PrivacyStatus,
  TikTokPostProfileSettings,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_PINTEREST
} from 'shared'
import {
  ComposerInstagramPost, ComposerPinterestPost, FBAlbum, LocationInfo, PINTEREST_AI_SYNTHETIC_PERFORMER, PinterestBoard
} from 'shared/types'
import styles from './NetworkOptionsPopup.pcss'
import { PostDestination } from 'shared'

export interface NetworkOptionsSelection {
  fbAlbums: Record<string, FBAlbum | undefined>
  piBoards: Record<string, PinterestBoard>
  igLocations: Record<string, LocationInfo | undefined>
  tkSettings: Record<string, TikTokPostProfileSettings>
  ytPrivacy: PrivacyStatus
  fbPostType: string | undefined
  igPostType: string | undefined
  /** `undefined` when the AI content checkbox was left untouched - the posts keep their own flags. */
  aiContent: boolean | undefined
  /** `undefined` when the Pinterest AI-generated person checkbox was left untouched - the pins keep their own mode. */
  piSyntheticPerformer: boolean | undefined
}

interface NetworkOptionsPopupProps {
  open: boolean
  posts: BulkContentPost[]
  profiles: { [network: string]: PostDestination[] }
  onCancel: () => void
  onConfirm: (selection: NetworkOptionsSelection) => void
}

export function NetworkOptionsPopup(props: NetworkOptionsPopupProps) {
  const { posts } = props
  const boards = useSelector(pinterestProfilesBoardsSelector)
  const [selectedFBAlbums, setSelectedFBAlbums] = useState<Record<string, FBAlbum | undefined>>({})
  const [selectedPIBoards, setSelectedPIBoards] = useState<Record<string, PinterestBoard>>({})
  const [selectedIGLocations, setSelectedIGLocations] = useState<Record<string, LocationInfo | undefined>>({})
  const [tkSettings, setTkSetings] = useState<Record<string, TikTokPostProfileSettings>>({})
  const [hasTikTokPhotos, setHasTikTokPhotos] = useState(false)
  const [hasTikTokVideos, setHasTikTokVideos] = useState(false)
  const [tikTokPhotosOnly, setTikTokPhotosOnly] = useState(false)
  const [ytPrivacyStatus, setPrivacyStatus] = useState('public')
  const [fbPostType, setFBPostType] = useState('post')
  const [igPostType, setIGPostType] = useState('post')
  // Undefined until the checkbox is used - confirming without touching it must not change any post's flag
  const [aiContent, setAiContent] = useState<boolean | undefined>(undefined)
  const [piSyntheticPerformer, setPISyntheticPerformer] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    const igPostsWithLocations = posts.filter(post => {
      return post.network === BRAND_INSTAGRAM && Object.keys((post.data as ComposerInstagramPost).locations || {}).length > 0
    })
    if (igPostsWithLocations.length > 0) {
      setSelectedIGLocations((igPostsWithLocations[0].data as ComposerInstagramPost).locations)
    }
  }, [posts])

  let hasFBImagePost = false
  // EXPL: Facebook ignores the AI content flag on photo posts - a video with a thumbnail image is not one
  let hasFBPhotoPost = false
  const postsByNetwork = props.posts.reduce((map: Record<string, BulkContentPost[]>, post) => {
    if (post.network === 'facebook' && post.imageUrl) {
      hasFBImagePost = true
      hasFBPhotoPost = hasFBPhotoPost || !post.videoUrl
    }
    if (map[post.network]) {
      map[post.network].push(post)
    } else {
      map[post.network] = [post]
    }
    return map
  }, {})

  const networks = Object.keys(postsByNetwork)
  const hasPIOptions = networks.includes(BRAND_PINTEREST)
  const hasIGOptions = networks.includes(BRAND_INSTAGRAM)
  const hasTKOptions = networks.includes(BRAND_TIKTOK)
  const hasYTOptions = networks.includes(BRAND_YOUTUBE)
  const aiContentPosts = props.posts.filter(post => AI_CONTENT_NETWORKS.includes(post.network))
  const hasAiContentOptions = aiContentPosts.length > 0
  // EXPL: starts checked only when every selected post is already flagged. Mixed flags show unchecked, and stay as they
  // are unless the checkbox is used - it is deliberately not a "clear all" for an untouched confirm.
  const aiContentChecked = aiContent ?? (hasAiContentOptions && aiContentPosts.every(post => post.isAiContent))
  // EXPL: same rule as the AI content flag - checked only when every selected pin already has the mode
  const piSyntheticPerformerChecked = piSyntheticPerformer ?? (hasPIOptions && postsByNetwork[BRAND_PINTEREST].every(
    post => (post.data as ComposerPinterestPost).aiContentMode === PINTEREST_AI_SYNTHETIC_PERFORMER
  ))
  const isEmpty = !hasFBImagePost && !hasPIOptions && !hasIGOptions && !hasTKOptions && !hasYTOptions && !hasAiContentOptions
  const instagramProfiles = props.profiles[BRAND_INSTAGRAM] || []
  const locationEnabledProfiles = instagramProfiles.filter(p => !p.instagramDirect)

  const boardsByProfile = Object.keys(boards).reduce((map: Record<string, PinterestBoard[]>, ppid) => {
    map[ppid] = boards[ppid].map(b => ({ ...b, ppid, selected: selectedPIBoards[ppid]?.id === b.id }))
    return map
  }, {})

  useEffect(() => {
    const tiktokPosts = props.posts.filter(post => post.network === BRAND_TIKTOK)
    if (tiktokPosts.length > 0) {
      const hasPhotos = tiktokPosts.some(post => post.imageUrl)
      const hasVideos = tiktokPosts.some(post => post.videoUrl)
      setHasTikTokPhotos(hasPhotos)
      setHasTikTokVideos(hasVideos)
      setTikTokPhotosOnly(hasPhotos && !hasVideos)
    }
  }, [props.posts])

  const onSelectedAlbumChange = (p: PostDestination, a: FBAlbum) => {
    setSelectedFBAlbums(current => ({ ...current, [p.ppid]: a }))
  }

  const onSelectedBoardChange = (ppid: string, b: PinterestBoard) => {
    setSelectedPIBoards(current => ({ ...current, [ppid]: b }))
  }

  const onSelectedLocationChange = (location: LocationInfo | undefined, ppid: string) => {
    setSelectedIGLocations(current => ({ ...current, [ppid]: location }))
  }

  const onTkSettingsChange = useCallback((settings: TikTokPostProfileSettings) => {
    setTkSetings(current => ({ ...current, [settings.ppid]: settings }))
  }, [])

  const onConfirm = () => {
    props.onConfirm({
      fbAlbums: selectedFBAlbums,
      piBoards: selectedPIBoards,
      igLocations: selectedIGLocations,
      tkSettings,
      ytPrivacy: ytPrivacyStatus as PrivacyStatus,
      fbPostType: fbPostType === 'post' ? undefined : fbPostType,
      igPostType: igPostType === 'post' ? undefined : igPostType,
      aiContent,
      piSyntheticPerformer
    })
  }

  const privacySelectOptions: PPSelectOptions = {
    public: { label: <FormattedMessage id="composer.labels.youtube.switch-privacy.public" /> },
    private: { label: <FormattedMessage id="composer.labels.youtube.switch-privacy.private" /> },
    unlisted: { label: <FormattedMessage id="composer.labels.youtube.switch-privacy.unlisted" /> }
  }

  const fbPostTypeOptions = useMemo(() => {
    const options = [{
      value: 'post',
      label: 'Post'
    }]
    const showReel = props.posts.some(post => post.network === BRAND_FACEBOOK && post.videoUrl)
    const showStory = props.posts.some(post => post.network === BRAND_FACEBOOK && post.imageUrl)

    if (!showReel && !showStory) {
      return []
    }

    if (showReel) {
      options.push({
        value: FB_POST_TYPE_REEL,
        label: 'Reel'
      }, {
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    } else if (showStory) {
      options.push({
        value: POST_TYPE_STORY,
        label: 'Story'
      })
    }
    return options
  }, [props.posts])

  return (
    <PPDialog
      open={props.open}
      onClose={props.onCancel}
      size="lg"
      title={<FormattedMessage id="composer.multi-post.network-options-popup.title" />}
      footer={
        <div className={styles.actions}>
          <Button onClick={props.onCancel}>
            <FormattedMessage id={isEmpty ? 'actions.ok' : 'actions.cancel'} />
          </Button>
          {!isEmpty && (
            <Button color="primary" variant="contained" onClick={onConfirm}>
              <FormattedMessage id="actions.confirm" />
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.content}>
        {hasAiContentOptions && (
          <div className={styles.section}>
            <div className={styles.options}>
              <AiContentCheckbox checked={aiContentChecked} id="bulk-network-options-ai-content" onChange={setAiContent} />
              {hasFBPhotoPost && <AiContentFacebookPhotoNote className={styles.hint} />}
            </div>
          </div>
        )}
        {hasFBImagePost && (
          <div className={styles.section}>
            <h3 className={styles.title}>Facebook</h3>
            <div className={styles.options}>
              {props.profiles[BRAND_FACEBOOK].map(profile => (
                <div className={styles['option-box']} key={profile.id}>
                  <FacebookAlbumSelector
                    profile={profile}
                    raised
                    selectedAlbumsCustom={selectedFBAlbums[profile.ppid] ? [selectedFBAlbums[profile.ppid] as FBAlbum] : []}
                    onSelectedAlbumChangeCustom={onSelectedAlbumChange}
                  />
                </div>
              ))}

              {fbPostTypeOptions.length > 1 && (
                <div className={styles['option-box']}>
                  <PPSwitch
                    selectedValue={fbPostType}
                    options={fbPostTypeOptions}
                    onSelectedValueChange={setFBPostType}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {hasPIOptions && (
          <div className={styles.section}>
            <h3 className={styles.title}>Pinterest <PinterestRefreshBoardsButton /></h3>
            <div className={styles.options}>
              {props.profiles[BRAND_PINTEREST].map(p => (
                <div key={p.id} className={styles['option-box']}>
                  <PinterestBoardSelector
                    profile={p}
                    boards={boardsByProfile[p.ppid]}
                    raised
                    onSelectedBoardChange={onSelectedBoardChange}
                  />
                </div>
              ))}
            </div>
            <div className={styles.options}>
              <AiContentCheckbox
                checked={piSyntheticPerformerChecked}
                id="bulk-network-options-pinterest-ai-synthetic-performer"
                label={<FormattedMessage id="composer.labels.ai-synthetic-performer" />}
                onChange={setPISyntheticPerformer}
              />
            </div>
          </div>
        )}
        {hasIGOptions && (
          <div className={styles.section}>
            <h3 className={styles.title}>Instagram</h3>
            <div className={styles.options}>
              {locationEnabledProfiles.map(profile => (
                <div className={styles['option-box']} key={profile.id}>
                  <InstagramLocationSelector
                    profile={profile}
                    selectedLocation={selectedIGLocations[profile.ppid]}
                    raised
                    onSelectedLocationChange={onSelectedLocationChange}
                  />
                </div>
              ))}
            </div>
            <div className={styles.options}>
              <PPSwitch
                selectedValue={igPostType}
                options={[{
                  value: 'post',
                  label: 'Post/Reel'
                }, {
                  value: POST_TYPE_STORY,
                  label: 'Story'
                }]}
                onSelectedValueChange={setIGPostType}
              />
            </div>
          </div>
        )}
        {hasTKOptions && (
          <div className={`${styles.section} ${styles.tk}`}>
            <h3 className={styles.title}>TikTok</h3>
            {props.profiles[BRAND_TIKTOK].map(profile => (
              <TikTokProfileSettings
                key={profile.ppid}
                profile={profile}
                settings={tkSettings[profile.ppid]}
                ignoreSavedSettings
                hideSaveOption
                showLoader
                isPhotoPost={tikTokPhotosOnly}
                showPhotoOptions={hasTikTokPhotos}
                onChange={onTkSettingsChange}
              />
            ))}
            {!tikTokPhotosOnly && hasTikTokPhotos && hasTikTokVideos && (
              <div className={styles.note}>
                Note: Duet and Stitch apply only to video posts.
                <br />
                Auto-add music applies only to photo posts.
              </div>
            )}
          </div>
        )}
        {hasYTOptions && (
          <div className={styles.section}>
            <h3 className={styles.title}>YouTube</h3>
            <PPSelect
              name={<FormattedMessage id="composer.labels.youtube-privacystatus-placeholder" />}
              options={privacySelectOptions}
              selectedValue={ytPrivacyStatus}
              withCaret
              raised
              onSelectionChange={setPrivacyStatus}
            />
          </div>
        )}
        {isEmpty && (
          <h4 className={styles.title}>
            <FormattedMessage id="composer.multi-post.network-options-popup.empty" />
          </h4>
        )}
      </div>
    </PPDialog>
  )
}

export default NetworkOptionsPopup
